import React from "react";
import { useSelector, useDispatch } from "react-redux";
import { Link } from "react-router-dom";
import { Button, Pager } from "rhombus-react";
import ReactTooltip from "react-tooltip";
import { useHistory } from "react-router";

import { Libraries } from "reducers/Libraries";
import { hasActiveFilters } from "reducers/common/ColumnFilters";

import FilterTagList from "widgets/toolbars/FilterTagList";

import useCancelToken from "hooks/useCancelToken";
import { useClearLibraryFilters } from "hooks/useClearFilters";
import { useCollectionFiltering } from "utils/DataGrid";
import { LIBRARY_ROW_COUNT_OPTIONS } from "utils/constants";

import EmptySection from "widgets/EmptySection";
import Permissioned from "widgets/Permissioned";

import Toolbar from "./libraries/Toolbar";
import Grid from "./libraries/Grid";
import Modals from "./libraries/Modals";


import "./CreativeLibrariesScreen.css";

export function replaceSearchTerm(searchTerm) {
	let final = searchTerm.replace('creative:', '');

	const mediaBuyerIndex = final.indexOf(",mb:");
	if (mediaBuyerIndex > 0) {
		final = final.substring(0, mediaBuyerIndex);
	}

	return final.trim();
}

export default function CreativeLibrariesScreen(props){
	const dispatch = useDispatch()
		, {
			libraries,
			routeLocation,
		} = useSelector((state) => {
			return {
				libraries:state.libraries,
				routeLocation:state?.router?.location?.pathname,
			};
		})
		, hasActive = hasActiveFilters(libraries)
		, history = useHistory();

	const { getNewToken } = useCancelToken(true);
	const clearFilters = useClearLibraryFilters();

	const {
		filterInfo: { page, rpp, searchTerm, sort },
		columnFilters,
	} = useCollectionFiltering(
		libraries,
		Libraries.setAdvancedFilter,
		() => {
			dispatch(Libraries.loadLibraries(getNewToken()))
				.then((resp)=>{
					if(resp.success){
						ReactTooltip.rebuild();
						let { items } = resp.payload.data;
						openLibrary(searchTerm, items);
					}
				});
		 }
	);

	const hasFiltersAppliedClass = hasActive ? "has-filters-applied" : "";

	function openLibrary(searchTerm, items){
		if(searchTerm.includes('creative:') && items.length === 1){
			clearFilters(); // Clear the library filters before setting doing the search term replacement, or else we wipe it out
			history.push(`/library/${items[0].id}/`, { initialSearchTerm: replaceSearchTerm(searchTerm) });
			dispatch(Libraries.setSearchTerm(""));
		}
	}

	let screenPath = props?.match?.url
		, returnPath = `${screenPath}/`
		, $modals = (
			<Modals
				key="modals"
				screenPath = {screenPath}
				returnPath = {returnPath}
			/>
		)
		, $toolbar = (
			<Toolbar
				key="toolbar"
				searchTerm = {searchTerm}
				handleSearch = {newSearchTerm => dispatch(Libraries.setSearchTerm(newSearchTerm))}
				isLoading = {!libraries.isLoaded}
			/>
		)
		, $filterList = (
			<FilterTagList
				key="filters"
				Reducer = {Libraries}
				filters = {columnFilters}
			/>
		)
		;

	const pagerProps = {
		showItemCount: true,
		initialPage: page,
		forcePage: page,
		pageSize: rpp,
		pageCount: libraries.totalPages,
		maxItems: libraries.totalItems,
		showItemWord: "",
		rowCountItems: LIBRARY_ROW_COUNT_OPTIONS,

		disabled: !libraries.isLoaded,

		onPageChange: newPage => dispatch(Libraries.setPage(newPage)),
		onRowCountChange: newRPP => dispatch(Libraries.setRPP(newRPP)),
	};

	let displayWhat = [];

	if(libraries.isLoaded && libraries.totalItems === 0){
		let emptyProps = null;
		function resetAllFilters(){
			dispatch(Libraries.clearColumnFilters());
			dispatch(Libraries.setSearchTerm(""));
		}

		displayWhat = [$modals, $toolbar, $filterList];

		if(hasActive){
			emptyProps = {
				className: "empty-libraries no-filter-results",
				header: "No results to display",
				message: "Try adjusting your filters to find what you are looking for",
				src: "/images/noFiles.svg",
				buttons:[
					<Button key="clear" onClick={resetAllFilters}>Clear All Filters</Button>
				],
			};
		} else if(searchTerm){
			emptyProps = {
				className: "empty-libraries no-search-results",
				header: "No search results to display",
				message: "Try adjusting your search to find what you are looking for",
				src: "/images/noSearch.svg",
				buttons:[
					<Button key="clear" onClick={() => dispatch(Libraries.setSearchTerm(""))}>Clear Search</Button>
				],
			};
		} else {
			emptyProps = {
				className: "empty-libraries",
				header: "No Libraries have been created",
				message: "Start by selecting from the following options",
				src: "/images/noFiles.svg",
				buttons: [
					<Permissioned
						key="new-library"
						domain = "Creative Library"
						action = "Create">
						<Link to={`${routeLocation}modal/new/`} className="new-library-button">
							<Button style="block" theme="blue" icon="add">New Creative Library</Button>
						</Link>
					</Permissioned>
				],
			};
		}

		displayWhat = displayWhat.concat(<EmptySection key="empty-section" {...emptyProps} />);
	} else {
		displayWhat = [$modals, $toolbar, $filterList
			, <Grid
				key="grid"
				libraries = {libraries}
				isLoading = {!libraries.isLoaded}
				sort = {sort} // Needed to display the initial column sort order indicator
			/>
			, <Pager key="pager" {...pagerProps} />
		];
	}

	return <div className={`creative-libraries-screen ${hasFiltersAppliedClass}`}>{displayWhat}</div>;
}

CreativeLibrariesScreen.displayName = "CreativeLibrariesScreen";
